"use client";

import React, { useEffect, useState } from 'react';
import { formatLiveStatNumber } from './LiveStat';

/**
 * Delphina's trust signal: live assets under guidance, formatted the same way
 * as the home hero ("£Xm+ assets under guidance"). The figure comes from the
 * public stats endpoint; if it cannot be loaded the block renders nothing
 * rather than falling back to a made-up number (DEL-563 placeholder contract).
 *
 * Uses the non-animated `formatLiveStatNumber` primitive (no slot-machine
 * effect) because the surrounding card layout already carries visual weight
 * and animation here would compete with it.
 */
export default function TrustSignals() {
  const [assetsUnderGuidance, setAssetsUnderGuidance] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;

    // Same retry shape as the home hero's stats fetch in HomePageClient.
    const fetchStats = async (retries = 2) => {
      for (let attempt = 0; attempt <= retries; attempt++) {
        if (cancelled) return;
        try {
          const res = await fetch('/api/public-stats/stats');
          if (res.ok) {
            const data = await res.json();
            if (!cancelled && data?.total_assets_under_guidance > 0) {
              setAssetsUnderGuidance(data.total_assets_under_guidance);
            }
            return;
          }
        } catch {
          // Public, unauthenticated endpoint; on failure the block simply doesn't render.
        }
        if (attempt < retries) {
          await new Promise((resolve) => setTimeout(resolve, 1000 * (attempt + 1)));
        }
      }
    };
    fetchStats();

    return () => {
      cancelled = true;
    };
  }, []);

  const formatted = formatLiveStatNumber(assetsUnderGuidance, "currency-rounded-m");
  if (formatted === null) return null;

  return (
    <div className="border-y-2 border-gray-200 bg-white py-6 my-8">
      <div className="max-w-4xl mx-auto px-4 text-center">
        <div className="mb-4">
          <p className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-1">Trusted with</p>
          <p className="text-2xl md:text-3xl font-bold text-gray-900">{formatted}</p>
          <p className="text-sm text-gray-600 mt-1">assets under guidance on Delphina</p>
        </div>
      </div>
    </div>
  );
}
